import clsx from "clsx";
import { X } from "lucide-react";
import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

export type ToastTone = "info" | "ok" | "warn" | "err";

export type ToastMessage = {
	tone?: ToastTone;
	title?: string;
	text: string;
};

export const TOAST_PRESET = {
	Download: { tone: "info", title: "Mengunduh" },
	Saved: { tone: "ok", title: "Tersimpan" },
	Sent: { tone: "ok", title: "Terkirim" },
	Cancelled: { tone: "warn", title: "Dibatalkan" },
	Done: { tone: "ok" },
	Failed: { tone: "err", title: "Gagal" },
} as const satisfies Record<string, Omit<ToastMessage, "text">>;

type VisibleToast = ToastMessage & { toastId: number };

const TOAST_DURATION_MS: Record<ToastTone, number> = {
	ok: 5000,
	info: 6000,
	warn: 8000,
	err: 10000,
};

const MAX_VISIBLE_TOASTS = 4;
const CLOSE_TOAST_LABEL = "Tutup pemberitahuan";
const TOAST_REGION_LABEL = "Pemberitahuan";

type ToastContextValue = {
	showToast: (message: ToastMessage) => void;
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

let nextToastId = 0;

const globalToastListeners = new Set<(message: ToastMessage) => void>();

// For code outside React.
export function notifyToast(message: ToastMessage) {
	for (const listener of globalToastListeners) listener(message);
}

export function ToastProvider({ children }: { children: ReactNode }) {
	const [visibleToasts, setVisibleToasts] = useState<VisibleToast[]>([]);

	const closeToast = useCallback((toastId: number) => {
		setVisibleToasts((toasts) =>
			toasts.filter((toast) => toast.toastId !== toastId),
		);
	}, []);

	const showToast = useCallback(
		(message: ToastMessage) => {
			nextToastId += 1;
			const toastId = nextToastId;
			setVisibleToasts((toasts) =>
				[...toasts, { ...message, toastId }].slice(-MAX_VISIBLE_TOASTS),
			);
			setTimeout(
				() => closeToast(toastId),
				TOAST_DURATION_MS[message.tone ?? "info"],
			);
		},
		[closeToast],
	);

	useEffect(() => {
		globalToastListeners.add(showToast);
		return () => {
			globalToastListeners.delete(showToast);
		};
	}, [showToast]);

	const contextValue = useMemo(() => ({ showToast }), [showToast]);

	return (
		<ToastContext.Provider value={contextValue}>
			{children}
			<section
				className="k3a k3a-alerts"
				aria-label={TOAST_REGION_LABEL}
				aria-live="polite"
			>
				{visibleToasts.map((toast) => {
					const tone = toast.tone ?? "info";
					return (
						<div
							key={toast.toastId}
							className={clsx("k3a-alert pop", tone !== "info" && tone)}
							role={tone === "err" ? "alert" : "status"}
						>
							<span className="tx">
								{toast.title && <b>{toast.title}</b>}
								{toast.text}
							</span>
							<button
								type="button"
								className="x"
								aria-label={CLOSE_TOAST_LABEL}
								onClick={() => closeToast(toast.toastId)}
							>
								<X width={16} height={16} aria-hidden="true" />
							</button>
						</div>
					);
				})}
			</section>
		</ToastContext.Provider>
	);
}

export function useToast() {
	const contextValue = useContext(ToastContext);
	if (!contextValue) throw new Error("useToast needs ToastProvider");
	return contextValue;
}
